import React, { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { useLang } from "@/context/Lang";
import { CONTACT } from "@/data";
import logo from "@/assets/logo.png";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "gallery", label: t.nav.gallery },
    { id: "why", label: t.nav.why },
    { id: "reviews", label: t.nav.reviews },
    { id: "quote", label: t.nav.book },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-navy/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center group" data-testid="logo">
          <img src={logo} alt="Seven Home Services" className="h-9 md:h-11 w-auto drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)] transition-transform group-hover:scale-[1.03]" />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-semibold text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="text-xs font-bold text-white/80 border border-white/20 rounded-full px-3 py-1.5 hover:border-orange hover:text-orange transition-colors"
          >
            {lang === "pt" ? "EN" : "PT"}
          </button>
          <button
            onClick={() => scrollTo("quote")}
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-orange hover:bg-[#1550C0] text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
          >
            {t.nav.cta}
          </button>
          <a href={CONTACT.phoneHref} className="md:hidden text-white p-2" data-testid="nav-call">
            <Phone size={20} />
          </a>
          <button className="md:hidden text-white p-1" onClick={() => setOpen((o) => !o)} data-testid="nav-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-navy/95 backdrop-blur-xl border-t border-white/10 px-4 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-${l.id}`}
              onClick={() => { scrollTo(l.id); setOpen(false); }}
              className="text-left py-3 text-white/80 font-semibold border-b border-white/5"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => { scrollTo("quote"); setOpen(false); }}
            className="mt-3 bg-orange text-white font-bold py-3 rounded-full"
          >
            {t.nav.cta}
          </button>
        </div>
      )}
    </header>
  );
}
